import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import {HttpSearchResultFilterPrices, State} from "../../../models";
import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import { SearchService } from '../../../services/search/search.service';
import {ToursStateService} from "../../../services/tours-state.service";
import IMask, {InputMask} from "imask";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {AnalyticService} from "../../../services/analytic.service";
import { PriceFormatPipe } from '../../pipes/price-format/price-format.pipe';

@Component({
  selector: 'app-price-filter',
  templateUrl: './price-filter.component.html',
  styleUrls: ['./price-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgxSliderModule,
    PriceFormatPipe
  ],
  standalone: true
})
export class PriceFilterComponent extends OnDestroy$ implements OnInit, OnDestroy {
  @Input() filterPrices: HttpSearchResultFilterPrices;
  @Output() filterChanged = new EventEmitter<void>();

  @ViewChild('minInput', { static: true }) minInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('maxInput', { static: true }) maxInputRef!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  private analyticDebounceTimeout: any;
  private currentState: State;
  private isSyncingInputMasks = false;
  private isMasksInitialized = false;

  minSliderValue: number;
  maxSliderValue: number;

  minInputValue: number;
  maxInputValue: number;

  minInputElementMask: InputMask;
  maxInputElementMask: InputMask;

  options: Options = {
    stepsArray: [],
    translate: (): string => ``,
  };

  constructor(
    private state: ToursStateService,
    private cdRef: ChangeDetectorRef,
    private searchService: SearchService,
    private analyticService: AnalyticService
  ) {
    super();
  }

  ngOnInit(): void {
    this.currentState = this.state.getState();

    this.setSliderSelectedMinValue();
    this.setSliderSelectedMaxValue();
    this.setInputValues();

    this.options.floor = this.filterPrices.min.value;
    this.options.ceil = this.filterPrices.max.value;

    this.initializeSlider(this.options.floor, this.options.ceil);

    this.initMasks();

    this.state.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state.applyedFilters.priceMin === 0 && state.applyedFilters.priceMax === 0) {
          this.resetSliderToDefault();
        }
      });
  }

  ngAfterViewInit() {
    // На случай если ViewChild не сработал в ngOnInit
    this.initMasks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.minInputElementMask?.destroy();
    this.maxInputElementMask?.destroy();
  }

  private initMasks(): void {
    if (this.isMasksInitialized) {
      return;
    }

    this.isMasksInitialized = true;
    this.minInputElementMask = IMask(this.minInputRef.nativeElement, {
      mask: Number,
      scale: 0,
      signed: false,
      thousandsSeparator: ' ',
      padFractionalZeros: false,
      normalizeZeros: true,
      radix: ',',
    });

    this.maxInputElementMask = IMask(this.maxInputRef.nativeElement, {
      mask: Number,
      scale: 0,
      signed: false,
      thousandsSeparator: ' ',
      padFractionalZeros: false,
      normalizeZeros: true,
      radix: ',',
    });

    this.minInputElementMask.on('accept', () => {
      if (this.isSyncingInputMasks) {
        return;
      }

      this.minInputValue = Number(this.minInputElementMask.unmaskedValue) || 0;
      this.updateMinInputValue();
    });

    this.maxInputElementMask.on('accept', () => {
      if (this.isSyncingInputMasks) {
        return;
      }

      this.maxInputValue = Number(this.maxInputElementMask.unmaskedValue) || 0;
      this.updateMaxInputValue();
    });

    this.syncInputMasks();
  }

  changeSlider(params: any): void {
    this.setPriceValues(params.value, params.highValue);
  }

  hasSelectedFilter(): boolean {
    return this.state.hasSelectedFilter('priceMin') || this.state.hasSelectedFilter('priceMax');
  }

  resetFilter(): void {
    this.state.resetFilter('priceMin');
    this.state.resetFilter('priceMax');
    this.resetSliderToDefault();
    this.filterChanged.emit();
  }

  commitMinInputValue(): void {
    const value = this.normalizeSliderValue(
      this.minInputElementMask.unmaskedValue,
      this.minSliderValue
    );
    this.setPriceValues(Math.min(value, this.maxSliderValue), this.maxSliderValue, true);
  }

  commitMaxInputValue(): void {
    const value = this.normalizeSliderValue(
      this.maxInputElementMask.unmaskedValue,
      this.maxSliderValue
    );
    this.setPriceValues(this.minSliderValue, Math.max(value, this.minSliderValue), true);
  }

  private updateMinInputValue(): void {
    const value = this.normalizeSliderValue(
      this.minInputElementMask.unmaskedValue,
      this.minSliderValue
    );
    this.setPriceValues(Math.min(value, this.maxSliderValue), this.maxSliderValue, false);
  }

  private updateMaxInputValue(): void {
    const value = this.normalizeSliderValue(
      this.maxInputElementMask.unmaskedValue,
      this.maxSliderValue
    );
    this.setPriceValues(this.minSliderValue, Math.max(value, this.minSliderValue), false);
  }

  private setPriceValues(minValue: number, maxValue: number, syncInputs = true): void {
    this.minSliderValue = minValue;
    this.maxSliderValue = maxValue;

    this.updateStatePriceMin(this.minSliderValue);
    this.updateStatePriceMax(this.maxSliderValue);
    this.filterChanged.emit();

    if (syncInputs) {
      this.syncInputMasks();
    }


    this.cdRef.detectChanges();
  }

  private setSliderSelectedMinValue(): void {
    this.minSliderValue = this.currentState.selectedFilters.priceMin > 0
      ? this.currentState.selectedFilters.priceMin
      : this.filterPrices.min.value;
  }

  private setSliderSelectedMaxValue(): void {
    this.maxSliderValue = this.currentState.selectedFilters.priceMax > 0
      ? this.currentState.selectedFilters.priceMax
      : this.filterPrices.max.value;
  }

  private setInputValues(): void {
    this.minInputValue = this.minSliderValue;
    this.maxInputValue = this.maxSliderValue;
  }

  private resetSliderToDefault(): void {
    this.minSliderValue = this.filterPrices.min.value;
    this.maxSliderValue = this.filterPrices.max.value;

    this.syncInputMasks();

    this.cdRef.detectChanges();
  }

  private initializeSlider(min: number, max: number): void {
    this.options.stepsArray = this.generateSteps(min, max);
  }

  private generateSteps(min: number, max: number): { value: number }[] {
    const steps: { value: number }[] = [];
    let current = min;

    while (current < min + (max - min) * 0.1) {
      steps.push({ value: current });
      current = this.roundToNearest(current + 20000, 20000);
    }

    while (current < min + (max - min) * 0.4) {
      steps.push({ value: current });
      current = this.roundToNearest(current + 100000, 100000);
    }

    while (current <= max) {
      steps.push({ value: current });
      current = this.roundToNearest(current + 300000, 300000);
    }

    if (steps[steps.length - 1].value !== max) {
      steps.push({ value: max });
    }

    return steps;
  }

  private roundToNearest(value: number, step: number): number {
    return Math.ceil(value / step) * step;
  }

  private normalizeSliderValue(value: string | number, fallbackValue: number): number {
    const numericValue = Number(value);
    const valueInRange = Number.isFinite(numericValue)
      ? Math.min(Math.max(numericValue, this.options.floor), this.options.ceil)
      : fallbackValue;

    return this.getNearestStepValue(valueInRange);
  }

  private getNearestStepValue(value: number): number {
    const steps = this.options.stepsArray || [];
    if (!steps.length) {
      return value;
    }

    return steps.reduce((nearest, step) =>
      Math.abs(step.value - value) < Math.abs(nearest - value)
        ? step.value
        : nearest,
      steps[0].value
    );
  }

  private syncInputMasks(): void {
    if (!this.minInputElementMask || !this.maxInputElementMask) {
      return;
    }

    this.isSyncingInputMasks = true;
    this.minInputElementMask.typedValue = this.minSliderValue;
    this.maxInputElementMask.typedValue = this.maxSliderValue;
    this.minInputValue = this.minSliderValue;
    this.maxInputValue = this.maxSliderValue;
    this.isSyncingInputMasks = false;
  }

  private updateStatePriceMin(value: number): void {
    this.state.toggleFilterValue('priceMin', value);
  }

  private updateStatePriceMax(value: number): void {
    this.state.toggleFilterValue('priceMax', value);
  }
}
